import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { take } from 'rxjs';

import { FileService } from '../../../shared/services/file.service';
import { StorageService } from '../../../shared/services/storage.service';
import { ModalPdfViewerComponent } from '../../../shared/components/modal-pdf-viewer/modal-pdf-viewer.component';

type FileStatus = 'PENDING' | 'PENDING_UPLOAD' | 'APPROVED' | 'REJECTED' | 'ALL';

@Component({
  selector: 'app-review-files',
  templateUrl: './review-files.component.html',
  styleUrls: ['./review-files.component.scss'],
})
export class ReviewFilesComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  displayedColumns: string[] = [
    'professional',
    'name',
    'status',
    'createdAt',
    'actions',
  ];

  dataSource = new MatTableDataSource<any>([]);
  loading = false;

  status: FileStatus = 'PENDING';

  total = 0;
  page = 1;
  limit = 20;

  readonly statusOptions: Array<{ value: FileStatus; label: string }> = [
    { value: 'PENDING', label: 'Pendente de análise' },
    { value: 'PENDING_UPLOAD', label: 'Aguardando envio' },
    { value: 'REJECTED', label: 'Rejeitado' },
    { value: 'APPROVED', label: 'Aprovado' },
    { value: 'ALL', label: 'Todos' },
  ];

  private userRole: string | null = null;

  constructor(
    private readonly fileService: FileService,
    private readonly storageService: StorageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly snackbar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.userRole = this.storageService.getUser()?.role ?? null;

    this.route.queryParams.pipe(take(1)).subscribe((params: any) => {
      const statusParam = params?.status as FileStatus | undefined;
      this.status = statusParam || 'PENDING';
      this.load();
    });
  }

  isAdmin(): boolean {
    return String(this.userRole || '').toUpperCase() === 'ADMIN';
  }

  onStatusChange(): void {
    this.page = 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { status: this.status === 'ALL' ? null : this.status },
      queryParamsHandling: 'merge',
    });
    this.load();
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onPage(event: PageEvent): void {
    this.page = event.pageIndex + 1;
    this.limit = event.pageSize;
    this.load();
  }

  refresh(): void {
    this.load();
  }

  openProfessional(professionalId: string): void {
    this.router.navigate(['/professionals', professionalId]);
  }

  viewFile(file: any): void {
    const maxWidth = window.innerWidth * 0.85;
    const maxHeight = window.innerHeight * 0.85;
    this.dialog.open(ModalPdfViewerComponent, {
      data: { fileId: file._id, mimetype: file.mimetype, professionalId: file.professional?._id },
      maxWidth: `${maxWidth}px`,
      maxHeight: `${maxHeight}px`,
      height: 'auto',
      width: 'auto',
      panelClass: 'document-viewer-dialog',
    });
  }

  downloadFile(file: any): void {
    const professionalName = file.professional?.fullname || 'profissional';
    const filename = `${professionalName}-${file.name}`;
    this.fileService.download(file._id, file.mimetype, filename).subscribe(
      () => this.snackbar.open('Download iniciado', 'OK', { duration: 2500 }),
      () => this.snackbar.open('Não foi possível baixar o arquivo', 'OK', { duration: 3000 })
    );
  }

  approve(file: any): void {
    this.fileService.check('APPROVED', file._id).subscribe(
      () => {
        this.snackbar.open('Arquivo aprovado', 'OK', { duration: 2500 });
        this.load();
      },
      () => this.snackbar.open('Falha ao aprovar arquivo', 'OK', { duration: 3000 })
    );
  }

  reject(file: any): void {
    this.fileService.check('REJECTED', file._id).subscribe(
      () => {
        this.snackbar.open('Arquivo rejeitado', 'OK', { duration: 2500 });
        this.load();
      },
      () => this.snackbar.open('Falha ao rejeitar arquivo', 'OK', { duration: 3000 })
    );
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'PENDING':
        return 'Pendente';
      case 'PENDING_UPLOAD':
        return 'Aguardando envio';
      case 'APPROVED':
        return 'Aprovado';
      case 'REJECTED':
        return 'Rejeitado';
      default:
        return status;
    }
  }

  private load(): void {
    this.loading = true;
    const status = this.status === 'ALL' ? undefined : this.status;
    this.fileService.list({ status, page: this.page, limit: this.limit }).subscribe({
      next: (result: any) => {
        const files = result?.files ?? [];
        this.total = result?.total ?? files.length;
        this.dataSource = new MatTableDataSource<any>(files);
        this.dataSource.filterPredicate = (data, filter) => {
          const hay = `${data?.name ?? ''} ${data?.description ?? ''} ${data?.fieldname ?? ''} ${data?.professional?.fullname ?? ''} ${data?.professional?.email ?? ''}`.toLowerCase();
          return hay.includes(filter);
        };
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackbar.open('Não foi possível carregar arquivos', 'OK', { duration: 3000 });
      },
    });
  }
}

